import { FC, useEffect, useRef } from 'react';
import moment from 'moment';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as EventSeatIcon } from 'assets/icons/event_seat.svg';
import { ReactComponent as TimeIcon } from 'assets/icons/time-line.svg';
import { datetimeFormat } from 'utils/datetime';
import { EVENT_DETAIL_TITLE } from 'utils/constants';

interface CardDetailProps {
  data: any;
  onClose: () => void;
  className?: string;
  title?: string;
}

const EventCardDetail: FC<CardDetailProps> = ({
  data,
  onClose,
  className,
  title = EVENT_DETAIL_TITLE,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  });

  useEffect(() => {
    const handlePressEscapeButton = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
    };

    window.addEventListener('keyup', handlePressEscapeButton);

    return () => {
      window.removeEventListener('keyup', handlePressEscapeButton);
    };
  }, [onClose]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const containerElement = containerRef.current;
      if (!containerElement) return;
      const isUserClickInsideContainer = containerElement.contains(
        e.target as Node
      );
      if (!isUserClickInsideContainer) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onClose]);

  return (
    <div
      className={`absolute z-[100] rounded-[1.6rem] p-3 shadow-elevation-08 bg-white -translate-y-[70%] w-[40rem] overflow-auto scrollbar ${className}`}
      ref={containerRef}
    >
      <button onClick={onClose} className="top-3 right-3 absolute">
        <CloseIcon />
      </button>

      <section className="space-y-1">
        <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
          {title}
        </h3>

        {data.name && <div className="font-bold">{data.name}</div>}

        <div className="flex gap-x-1 items-center">
          <TimeIcon className="w-2 h-2 all-child:fill-mid-grey" />
          <span>
            {datetimeFormat({
              dateString: data.appointmentDate,
              pattern: 'MMM DD, YYYY',
            })}{' '}
            {moment(data.startTime, 'hh:mm A').format('hh:mmA')} {' - '}
            {moment(data.endTime, 'hh:mm A').format('hh:mmA')}
          </span>
        </div>

        <div className="flex gap-x-1 items-center">
          <EventSeatIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
          <span>{data.operatory.name}</span>
        </div>

        {data.externalId && (
          <div className="flex gap-x-1 items-center">
            <span>Appt ID:</span>
            <span>{data.externalId}</span>
          </div>
        )}
      </section>
    </div>
  );
};

export default EventCardDetail;
